import React, { useState, useEffect } from 'react';
import { ShoppingCart as CartIcon, Plus, Minus, Trash2, Clock, CreditCard, Tag, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { CustomerInfo, CustomerInfoForm } from '@/components/relocation/CustomerInfoForm';
import { useEnhancedCartTracking } from '@/hooks/useEnhancedCartTracking';
import { useCart } from '@/contexts/CartContext';
import { useToast } from '@/hooks/use-toast';
import { useI18n } from '@/contexts/I18nContext';
import { supabase } from '@/integrations/supabase/client';
import { CartProgressIndicator } from './CartProgressIndicator';
import { RemoveItemConfirmation } from './RemoveItemConfirmation';
import { CartEmptyState } from './CartEmptyState';
import { CartItemCard } from './CartItemCard';
import { TrustBadges } from './TrustBadges';
import { PaymentLoadingOverlay } from './PaymentLoadingOverlay';
import { motion, AnimatePresence } from 'framer-motion';
import { CartItem } from '@/contexts/CartContext';

export const ShoppingCart: React.FC = () => {
  const { 
    items, 
    numberOfPeople, 
    colaboradorCode, 
    updateQuantity, 
    removeItem, 
    clearCart, 
    updatePeople, 
    checkout,
    isOpen,
    setIsOpen
  } = useCart();
  
  const [showCheckoutForm, setShowCheckoutForm] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [timeLeft, setTimeLeft] = useState(15 * 60); // 15 minutos en segundos
  const [itemToRemove, setItemToRemove] = useState<CartItem | null>(null);
  const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);
  
  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<{
    code: string;
    discountType: 'percentage' | 'fixed';
    discountValue: number;
  } | null>(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState('');
  
  const { trackCheckoutStart, trackRemoveFromCart } = useEnhancedCartTracking();
  const { toast } = useToast();
  const { t } = useI18n();
  const c = t.cart;

  // Timer para ofertas especiales
  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [timeLeft]);

  // Formatear tiempo restante
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Calculate totals
  const subtotal = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const baseTotal = subtotal * numberOfPeople;
  
  // Apply coupon discount
  const calculateDiscount = () => {
    if (!appliedCoupon) return 0;
    if (appliedCoupon.discountType === 'percentage') {
      return baseTotal * (appliedCoupon.discountValue / 100);
    }
    return Math.min(appliedCoupon.discountValue, baseTotal);
  };
  
  const discount = calculateDiscount();
  const total = Math.max(0, baseTotal - discount);

  // Apply coupon
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    
    setCouponLoading(true);
    setCouponError('');
    
    try {
      const { data: coupon, error } = await supabase
        .from('coupons')
        .select('*')
        .eq('code', couponCode.toUpperCase())
        .eq('is_active', true)
        .single();
      
      if (error || !coupon) {
        setCouponError(c.couponInvalid);
        return;
      }
      
      // Check expiration
      if (coupon.expires_at && new Date(coupon.expires_at) < new Date()) {
        setCouponError(c.couponExpired);
        return;
      }
      
      // Check max uses
      if (coupon.max_uses && coupon.current_uses >= coupon.max_uses) {
        setCouponError(c.couponMaxUses);
        return;
      }
      
      // Check minimum purchase
      if (coupon.min_purchase_amount && baseTotal < coupon.min_purchase_amount) {
        setCouponError(c.couponMinPurchase.replace('{amount}', String(coupon.min_purchase_amount)));
        return;
      }
      
      setAppliedCoupon({
        code: coupon.code,
        discountType: coupon.discount_type as 'percentage' | 'fixed',
        discountValue: coupon.discount_value,
      });
      
      toast({
        title: c.couponApplied,
        description: c.couponDiscount.replace('{discount}', coupon.discount_type === 'percentage' ? `${coupon.discount_value}%` : `${coupon.discount_value} CHF`),
      });
      
      setCouponCode('');
    } catch {
      setCouponError(c.couponError);
    } finally {
      setCouponLoading(false);
    }
  };
  
  const removeCoupon = () => {
    setAppliedCoupon(null);
    toast({ title: c.couponRemoved, description: c.couponRemovedDesc });
  };

  const handleRemoveItem = (id: string) => {
    const item = items.find(i => i.id === id);
    if (item) {
      setItemToRemove(item);
      setShowRemoveConfirmation(true);
    }
  };

  const confirmRemoveItem = async () => {
    if (itemToRemove) {
      await trackRemoveFromCart(
        { id: itemToRemove.id, title: itemToRemove.title, price: itemToRemove.price, quantity: itemToRemove.quantity },
        { items, totalAmount: total * 100, numberOfPeople, colaboradorRef: colaboradorCode }
      );
      removeItem(itemToRemove.id);
      setShowRemoveConfirmation(false);
      setItemToRemove(null);
      toast({ title: c.serviceRemoved, description: c.serviceRemovedDesc.replace('{title}', itemToRemove.title), duration: 2000 });
    }
  };

  const handlePriceChange = (_itemId: string, _newPrice: number) => {
    // Price change handler - totals are recalculated automatically
  };

  const handleCheckout = async (customerInfo: CustomerInfo) => {
    setProcessingPayment(true);

    try {
      await trackCheckoutStart(
        { items, totalAmount: total * 100, numberOfPeople, colaboradorRef: colaboradorCode },
        customerInfo.email
      );

      toast({ title: c.paymentMigration, description: c.paymentMigrationDesc, duration: 5000 });
    } catch (error) {
      console.error('💥 Error en el flujo del carrito:', error);
      toast({ title: c.paymentError, description: error instanceof Error ? error.message : c.paymentErrorDesc, variant: 'destructive', duration: 5000 });
    } finally { setProcessingPayment(false); }
  };

  // Determine current step based on state
  const getCurrentStep = () => {
    if (processingPayment) return 'payment';
    if (showCheckoutForm) return 'info';
    return 'cart';
  };

  return (
    <>
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md flex flex-col h-full">
          <SheetHeader className="flex-shrink-0">
            <SheetTitle className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <CartIcon className="h-5 w-5 shrink-0" />
                <span>{c.title}</span>
              </div>
              {items.length > 0 && !showCheckoutForm && (
                <Button variant="ghost" size="sm" onClick={clearCart} className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0 rounded-full" title={c.clearCart}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </SheetTitle>
            <SheetDescription>
              {items.length === 0 
                ? c.emptyCart 
                : `${items.length} ${items.length === 1 ? c.service : c.services} ${c.selected}`
              }
            </SheetDescription>
            
            {/* Progress Indicator */}
            {items.length > 0 && (
              <CartProgressIndicator currentStep={getCurrentStep()} />
            )}
          </SheetHeader>

          <div className="flex-1 flex flex-col min-h-0">
            {items.length === 0 ? (
              <CartEmptyState onClose={() => setIsOpen(false)} />
            ) : (
            <>
              {!showCheckoutForm ? (
                <div className="flex-1 flex flex-col min-h-0">
                   {/* Cart Items - Scrollable */}
                   <div className="flex-1 overflow-y-auto py-4 space-y-4 min-h-0">
                    {/* Cart Items with Animations */}
                    <AnimatePresence mode="popLayout">
                      <motion.div layout className="space-y-3">
                        {items.map((item) => (
                          <CartItemCard
                            key={item.id}
                            item={item}
                            onUpdateQuantity={updateQuantity}
                            onRemove={handleRemoveItem}
                            onPriceChange={handlePriceChange}
                          />
                        ))}
                      </motion.div>
                    </AnimatePresence>

                    <Separator />

                    {/* Marketing: Oferta limitada */}
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.3 }}
                    >
                      <Card className="border-amber-200/50 bg-gradient-to-r from-amber-50/80 to-orange-50/80 backdrop-blur-sm">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center">
                              <motion.div
                                animate={{ rotate: [0, 10, -10, 0] }}
                                transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
                              >
                                <Clock className="h-4 w-4 text-amber-600 mr-2 shrink-0" />
                              </motion.div>
                              <span className="font-semibold text-amber-800">{c.limitedOffer}</span>
                            </div>
                            <Badge variant="secondary" className="bg-amber-100 text-amber-800 text-xs font-mono">
                              {formatTime(timeLeft)}
                            </Badge>
                          </div>
                          <p className="text-sm text-amber-700">{c.addMoreServices}</p>
                        </CardContent>
                      </Card>
                    </motion.div>

                    {/* Trust Badges */}
                    <TrustBadges />
                  </div>

                  {/* Footer fijo - Price Summary and Actions */}
                  <div className="border-t pt-4 space-y-3 flex-shrink-0 bg-background">
                    {/* People Selector */}
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-medium">{c.numberOfPeople}</Label>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 w-7 p-0 rounded-full"
                          onClick={() => updatePeople(Math.max(1, numberOfPeople - 1))}
                        >
                          <Minus className="h-3 w-3 shrink-0" />
                        </Button>
                        <span className="text-sm font-medium w-6 text-center">{numberOfPeople}</span>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 w-7 p-0 rounded-full"
                          onClick={() => updatePeople(numberOfPeople + 1)}
                        >
                          <Plus className="h-3 w-3 shrink-0" />
                        </Button>
                      </div>
                    </div>

                    {/* Coupon Input */}
                    <div className="space-y-2">
                      {appliedCoupon ? (
                        <div className="flex items-center justify-between p-2 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 rounded-lg">
                          <div className="flex items-center gap-2">
                            <Check className="h-4 w-4 shrink-0 text-emerald-600" />
                            <span className="text-sm font-medium text-emerald-700 dark:text-emerald-400">
                              {appliedCoupon.code}
                            </span>
                            <Badge variant="secondary" className="text-xs">
                              -{appliedCoupon.discountType === 'percentage' 
                                ? `${appliedCoupon.discountValue}%` 
                                : `${appliedCoupon.discountValue} CHF`}
                            </Badge>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={removeCoupon}
                          >
                            <X className="h-3 w-3 shrink-0" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <div className="relative flex-1">
                            <Tag className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                            <Input
                              placeholder={c.couponPlaceholder}
                              value={couponCode}
                              onChange={(e) => {
                                setCouponCode(e.target.value.toUpperCase());
                                setCouponError('');
                              }}
                              className="pl-8 h-9 text-sm"
                              onKeyDown={(e) => e.key === 'Enter' && handleApplyCoupon()}
                            />
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-9 px-3"
                            onClick={handleApplyCoupon}
                            disabled={couponLoading || !couponCode.trim()}
                          >
                            {couponLoading ? '...' : c.apply}
                          </Button>
                        </div>
                      )}
                      {couponError && (
                        <p className="text-xs text-red-500">{couponError}</p>
                      )}
                    </div>

                      <div className="space-y-2 p-3 bg-muted/20 rounded-lg text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{c.subtotal}</span>
                        <span>{subtotal.toFixed(2)} CHF</span>
                      </div>
                      {numberOfPeople > 1 && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">× {numberOfPeople} {c.persons}</span>
                          <span>{baseTotal.toFixed(2)} CHF</span>
                        </div>
                      )}
                      {appliedCoupon && discount > 0 && (
                        <div className="flex justify-between text-emerald-600">
                          <span>{c.discount}</span>
                          <span>-{discount.toFixed(2)} CHF</span>
                        </div>
                      )}
                      <Separator className="my-2" />
                      <div className="flex justify-between font-bold text-base text-primary">
                        <span>{c.total}</span>
                        <motion.span
                          key={total}
                          initial={{ scale: 1.05 }}
                          animate={{ scale: 1 }}
                          transition={{ duration: 0.2 }}
                        >
                          {total.toFixed(2)} CHF
                        </motion.span>
                      </div>
                    </div>

                    <Button 
                      className="w-full bg-primary hover:bg-primary/90 h-11 text-sm font-medium"
                      onClick={() => setShowCheckoutForm(true)}
                    >
                      <CreditCard className="h-4 w-4 mr-2 shrink-0" />
                      {c.continue} • {total.toFixed(2)} CHF
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex-1 flex flex-col min-h-0">
                  <div className="flex-shrink-0 mb-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowCheckoutForm(false)}
                      className="flex items-center"
                    >
                      {c.backToCart}
                    </Button>
                  </div>
                  
                  <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0 pb-4">
                    <div className="h-full">
                      <CustomerInfoForm
                        onSubmit={handleCheckout}
                        onBack={() => setShowCheckoutForm(false)}
                        isLoading={processingPayment}
                      />
                    </div>
                  </div>
                </div>
               )}
             </>
           )}
         </div>
       </SheetContent>
     </Sheet>

      {/* Remove Item Confirmation Modal */}
      <RemoveItemConfirmation
        isOpen={showRemoveConfirmation}
        onClose={() => {
          setShowRemoveConfirmation(false);
          setItemToRemove(null);
        }}
        onConfirm={confirmRemoveItem}
        item={itemToRemove}
      />

      {/* Payment Loading Overlay */}
      <PaymentLoadingOverlay isVisible={processingPayment} />
   </>
  );
};